import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
    useGetSuperAdminClubsQuery,
    useGetSuperAdminTransactionsQuery,
    useImpersonateClubMutation,
    useContactClubAdminsMutation,
} from '../api/apiSlice';
import { useAppDispatch } from '../app/hooks';
import { startImpersonation } from '../features/auth/authSlice';
import { SuperAdminClubRow } from '../types/api';
import { formatShortDate } from '../utils/formatDate';

const TRANSACTION_STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
    paid: 'success',
    open: 'warning',
    pending: 'warning',
    failed: 'error',
    expired: 'error',
    canceled: 'error',
};

const BILLING_CHIP_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
    active: 'success',
    pending: 'warning',
    past_due: 'warning',
    failed_setup: 'error',
    canceled: 'error',
};

export default function SuperAdminDashboard() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { data: clubs, isLoading, isError } = useGetSuperAdminClubsQuery();
    const { data: transactions } = useGetSuperAdminTransactionsQuery();
    const [impersonateClub, { isLoading: isImpersonating }] = useImpersonateClubMutation();
    const [contactClubAdmins, { isLoading: isSending }] = useContactClubAdminsMutation();

    const totalRevenue = (transactions ?? [])
        .filter((tx) => tx.STATUS === 'paid' && tx.AMOUNT_CURRENCY === 'EUR')
        .reduce((sum, tx) => sum + parseFloat(tx.AMOUNT_VALUE), 0);

    const [contactTarget, setContactTarget] = useState<SuperAdminClubRow | null>(null);
    const [contactMessage, setContactMessage] = useState('');
    const [snackbar, setSnackbar] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

    const handleImpersonate = async (club: SuperAdminClubRow) => {
        try {
            const token = await impersonateClub(club.ID).unwrap();
            dispatch(startImpersonation(token));
            navigate('/administration');
        } catch (err: any) {
            setSnackbar({ severity: 'error', text: err?.data?.message ?? t('common.genericError') });
        }
    };

    const handleSendContact = async () => {
        if (!contactTarget || !contactMessage.trim()) return;

        try {
            const result = await contactClubAdmins({ clubId: contactTarget.ID, message: contactMessage.trim() }).unwrap();
            setSnackbar({
                severity: result.sent > 0 ? 'success' : 'error',
                text: result.sent > 0
                    ? t('superadmin.contactSent', { sent: result.sent, total: result.total })
                    : t('superadmin.contactNoneSent'),
            });
            setContactTarget(null);
            setContactMessage('');
        } catch (err: any) {
            setSnackbar({ severity: 'error', text: err?.data?.message ?? t('common.genericError') });
        }
    };

    const billingLabel = (status: string | null) =>
        status ? t(`superadmin.billing.${status}`, { defaultValue: status }) : t('superadmin.billing.notSetUp');

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (isError) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{t('superadmin.loadError')}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Typography variant="h5" gutterBottom>
                {t('superadmin.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('superadmin.subtitle')}
            </Typography>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('superadmin.club')}</TableCell>
                            <TableCell align="right">{t('superadmin.memberCount')}</TableCell>
                            <TableCell align="right">{t('superadmin.pendingCount')}</TableCell>
                            <TableCell>{t('superadmin.plan')}</TableCell>
                            <TableCell>{t('superadmin.billingStatus')}</TableCell>
                            <TableCell align="right">{t('superadmin.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {clubs?.map((club) => (
                            <TableRow key={club.ID} hover>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Avatar src={club.LOGO ?? undefined} sx={{ width: 28, height: 28 }}>
                                            {club.NAME.charAt(0)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2">{club.NAME}</Typography>
                                            {club.CITY && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {club.CITY}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="right">{club.MEMBER_COUNT}</TableCell>
                                <TableCell align="right">{club.PENDING_COUNT}</TableCell>
                                <TableCell>
                                    <Chip size="small" label={club.PLAN ?? 'starter'} variant="outlined" />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={billingLabel(club.BILLING_STATUS)}
                                        color={club.BILLING_STATUS ? BILLING_CHIP_COLOR[club.BILLING_STATUS] : 'default'}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Box display="flex" gap={1} justifyContent="flex-end">
                                        <Button size="small" variant="outlined" onClick={() => setContactTarget(club)}>
                                            {t('superadmin.contactAdmin')}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={isImpersonating}
                                            onClick={() => handleImpersonate(club)}
                                        >
                                            {t('superadmin.manageAsAdmin')}
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {clubs?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        {t('superadmin.noClubs')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" alignItems="baseline" justifyContent="space-between" sx={{ mt: 5 }}>
                <Typography variant="h6">{t('superadmin.transactionsTitle')}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('superadmin.totalRevenue', { amount: totalRevenue.toFixed(2) })}
                </Typography>
            </Box>

            <TableContainer component={Paper} sx={{ mt: 1 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('superadmin.club')}</TableCell>
                            <TableCell>{t('superadmin.plan')}</TableCell>
                            <TableCell align="right">{t('superadmin.amount')}</TableCell>
                            <TableCell>{t('superadmin.transactionStatus')}</TableCell>
                            <TableCell>{t('superadmin.paidAt')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transactions?.map((tx) => (
                            <TableRow key={tx.ID} hover>
                                <TableCell>{tx.CLUB_NAME}</TableCell>
                                <TableCell>
                                    <Chip size="small" label={tx.PLAN ?? '-'} variant="outlined" />
                                </TableCell>
                                <TableCell align="right">
                                    {tx.AMOUNT_VALUE} {tx.AMOUNT_CURRENCY}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={t(`superadmin.transactionStatuses.${tx.STATUS}`, { defaultValue: tx.STATUS })}
                                        color={TRANSACTION_STATUS_COLOR[tx.STATUS] ?? 'default'}
                                    />
                                </TableCell>
                                <TableCell>
                                    {tx.PAID_AT ? formatShortDate(tx.PAID_AT, i18n.language) : '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                        {transactions?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        {t('superadmin.noTransactions')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={contactTarget !== null} onClose={() => setContactTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>{t('superadmin.contactDialogTitle', { clubName: contactTarget?.NAME ?? '' })}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        multiline
                        minRows={4}
                        fullWidth
                        sx={{ mt: 1 }}
                        inputProps={{ maxLength: 2000 }}
                        placeholder={t('superadmin.contactPlaceholder')}
                        value={contactMessage}
                        onChange={(e) => setContactMessage(e.target.value)}
                        helperText={`${contactMessage.length}/2000`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setContactTarget(null)}>{t('common.cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={handleSendContact}
                        disabled={!contactMessage.trim() || isSending}
                    >
                        {t('superadmin.contactSend')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar !== null}
                autoHideDuration={5000}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar?.severity ?? 'success'} onClose={() => setSnackbar(null)}>
                    {snackbar?.text}
                </Alert>
            </Snackbar>
        </Container>
    );
}
